/**
 * Outreach hub filters, mirrored into the URL as query params so they survive a
 * round trip out to a campaign detail page and back (PRD 46). An absent field
 * means "all" / no filter — we never serialize the default so a clean hub URL
 * stays clean.
 */
import type { OutreachChannel, OutreachDisplayStatus } from "@/types/outreach";

export interface OutreachHubFilters {
	channel?: OutreachChannel;
	status?: OutreachDisplayStatus;
	q?: string;
}

const CHANNELS = new Set<string>([
	"email",
	"text_blast",
	"text_bank",
	"phone",
]);

const STATUSES = new Set<string>([
	"draft",
	"needs_setup",
	"scheduled",
	"running",
	"paused",
	"paused_credits",
	"done",
	"failed",
	"cancelled",
]);

/**
 * Parse and validate raw URL search into hub filters. Unknown values drop to
 * `undefined` so a hand-edited or stale URL can never wedge the list into a
 * permanently empty state.
 */
export function parseHubFilters(
	search: Record<string, unknown>,
): OutreachHubFilters {
	const channel =
		typeof search.channel === "string" && CHANNELS.has(search.channel)
			? (search.channel as OutreachChannel)
			: undefined;
	const status =
		typeof search.status === "string" && STATUSES.has(search.status)
			? (search.status as OutreachDisplayStatus)
			: undefined;
	const q =
		typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined;
	return { channel, status, q };
}
